import { logError } from '@/utils/logger';
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Trash, MagnifyingGlass, FolderOpen, FilePdf, FileDoc, FileXls, File as FileIcon, DownloadSimple, UploadSimple } from '@phosphor-icons/react';
import SearchableSelect from '@/components/SearchableSelect';

const CATEGORIES = [
  { value: 'presentation', label: 'Présentation' },
  { value: 'profil', label: "Profil de l'entreprise" },
  { value: 'reglement', label: "Règlement d'ordre intérieur" },
  { value: 'contrat', label: 'Contrat / Modèle' },
  { value: 'procedure', label: 'Procédure' },
  { value: 'autre', label: 'Autre' },
];

const fileIcon = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return <FilePdf size={22} className="text-[#EF4444]" weight="duotone" />;
  if (['doc', 'docx'].includes(ext)) return <FileDoc size={22} className="text-[#2563EB]" weight="duotone" />;
  if (['xls', 'xlsx'].includes(ext)) return <FileXls size={22} className="text-[#10B981]" weight="duotone" />;
  return <FileIcon size={22} className="text-[#94A3B8]" weight="duotone" />;
};

export default function Projects() {
  const [documents, setDocuments] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', category: 'autre' });
  const [pendingFile, setPendingFile] = useState(null);
  const fileRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchAll = useCallback(async () => {
    try {
      const [docsRes, meRes] = await Promise.all([
        fetch(`${API}/api/documents`, { credentials: 'include' }),
        fetch(`${API}/api/auth/me`, { credentials: 'include' }),
      ]);
      if (docsRes.ok) setDocuments(await docsRes.json());
      if (meRes.ok) setUserRole((await meRes.json()).role);
    } catch (error) { logError('Fetch documents:', error); }
  }, [API]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const canEdit = ['admin', 'drh', 'supervisor', 'finance'].includes(userRole);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pendingFile) { toast.error('Choisissez un fichier'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', pendingFile);
      const upRes = await fetch(`${API}/api/upload/document`, { method: 'POST', credentials: 'include', body: fd });
      if (!upRes.ok) { const err = await upRes.json(); throw new Error(err.detail || 'Upload échoué'); }
      const up = await upRes.json();
      const res = await fetch(`${API}/api/documents`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, file_id: up.file_id, filename: up.filename, size: up.size }),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Erreur'); }
      toast.success('Document importé');
      setShowDialog(false);
      setForm({ title: '', category: 'autre' });
      setPendingFile(null);
      fetchAll();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      const res = await fetch(`${API}/api/documents/${id}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Supprimé'); fetchAll(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur'); }
  };

  const catLabel = (v) => CATEGORIES.find(c => c.value === v)?.label || v;
  const filtered = documents.filter(d => !search || d.title?.toLowerCase().includes(search.toLowerCase()) || d.filename?.toLowerCase().includes(search.toLowerCase()) || catLabel(d.category).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#F8FAFC] tracking-tight">Projets & Documents</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">Présentation, profil, règlement d'ordre intérieur et autres documents</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowDialog(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="add-document-button">
            <UploadSimple size={18} className="mr-2" /> Importer un document
          </Button>
        )}
      </div>

      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un document..." className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] pl-9" data-testid="search-document" />
      </div>

      {filtered.length === 0 ? (
        <div className="bg-[#0F172A] border border-[#1E293B] rounded-md p-12 text-center">
          <FolderOpen size={44} className="mx-auto text-[#334155] mb-3" weight="duotone" />
          <p className="text-[#94A3B8]">Aucun document importé</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map(doc => (
            <div key={doc.doc_id} className="bg-[#0F172A] border border-[#1E293B] rounded-md p-4 flex items-start gap-3 hover:border-[#2563EB]/40 transition-colors" data-testid={`document-${doc.doc_id}`}>
              <div className="flex-shrink-0 mt-0.5">{fileIcon(doc.filename)}</div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#F8FAFC] truncate">{doc.title}</p>
                <p className="text-xs text-[#64748B] truncate">{doc.filename} • {(doc.size / 1024).toFixed(0)} Ko</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <Badge className="bg-[#8B5CF6]/20 text-[#8B5CF6] border border-[#8B5CF6]/30 text-xs">{catLabel(doc.category)}</Badge>
                  <span className="text-[10px] text-[#64748B]">{doc.uploaded_by_name}</span>
                </div>
              </div>
              <div className="flex flex-col gap-1 flex-shrink-0">
                <Button size="sm" variant="ghost" onClick={() => window.open(`${API}/api/files/${doc.file_id}`, '_blank')} className="text-[#10B981] hover:text-white hover:bg-[#10B981] h-8 w-8 p-0" title="Télécharger" data-testid={`download-doc-${doc.doc_id}`}>
                  <DownloadSimple size={15} />
                </Button>
                {userRole === 'admin' && (
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(doc.doc_id)} className="text-[#EF4444] hover:text-white hover:bg-[#EF4444] h-8 w-8 p-0"><Trash size={15} /></Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#0F172A] border-[#1E293B] text-[#F8FAFC] max-w-md">
          <DialogHeader><DialogTitle className="text-xl font-bold">Importer un document</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-[#F8FAFC]">Titre du document *</Label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required placeholder="Ex: Règlement d'ordre intérieur 2026" className="bg-[#1E293B] border-[#334155] text-[#F8FAFC]" data-testid="document-title" />
            </div>
            <div>
              <Label className="text-[#F8FAFC]">Catégorie</Label>
              <div className="mt-1">
                <SearchableSelect value={form.category} onValueChange={v => setForm({ ...form, category: v })} options={CATEGORIES} testId="document-category-select" />
              </div>
            </div>
            <div>
              <Label className="text-[#F8FAFC]">Fichier (PDF, Word, Excel... max 15 Mo) *</Label>
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png" onChange={e => setPendingFile(e.target.files?.[0] || null)} className="hidden" data-testid="document-file-input" />
              <button type="button" onClick={() => fileRef.current?.click()} className="w-full mt-1 p-4 border-2 border-dashed border-[#334155] rounded-md text-sm text-[#94A3B8] hover:border-[#2563EB] transition-colors" data-testid="document-file-picker">
                {pendingFile ? `📄 ${pendingFile.name}` : 'Cliquez pour choisir un fichier'}
              </button>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="border-[#334155] text-[#F8FAFC] hover:bg-[#1E293B]">Annuler</Button>
              <Button type="submit" disabled={uploading} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-document-button">
                {uploading ? 'Import en cours...' : 'Importer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
